/**
 * Cercevo AR launcher.
 *
 * Shopify's native "View in your space" button (ShopifyXR / Shopify.loadFeatures)
 * has a confirmed, still-unresolved bug in Horizon-family themes where the
 * required per-media JSON island is never rendered, so the button never
 * activates regardless of device support:
 * https://community.shopify.dev/t/horizon-theme-ar-bug/33924
 *
 * - iOS: the <a rel="ar"> anchor rendered in product-media.liquid IS the visible
 *   control and needs no JS at all — WebKit's AR Quick Look triggers natively on a
 *   real tap. The generic <button> is hidden entirely on iOS.
 * - Android: no equivalent native anchor exists, so this script shows the button
 *   and drives a Google Scene Viewer intent:// URL on click, with an https://
 *   fallback if the page is still visible shortly after (common inside
 *   embedded/in-app webviews that silently swallow intent:// navigations).
 * - Desktop (neither iOS nor Android): AR literally cannot launch from a desktop
 *   browser, so instead of pretending otherwise the button opens a small dialog
 *   with a QR code that encodes the CURRENT page URL (selected variant included,
 *   since Shopify's variant picker updates the URL query string on selection).
 *   Scanning it with a phone camera opens this same product page on the visitor's
 *   phone, where the iOS/Android paths above take over immediately. The QR is
 *   rendered client-side with a vendored copy of kazuhikoarase/qrcode-generator
 *   (assets/cercevo-qrcode.js, MIT licensed) — no external request, no data sent
 *   to a third-party QR service.
 *
 * 2026-09-03 (AR/3D görünüm): replaced the old "use your phone" text-only popup
 * (which auto-hid after 3s — too short to ever be useful) with the QR dialog
 * described above. If assets/cercevo-qrcode.js fails to load for any reason,
 * this degrades gracefully back to the original short explanatory text so the
 * button never ends up doing nothing.
 *
 * 2026-09-03 (stardust): the dialog's darkened backdrop now carries a dense
 * twinkling star-dust layer (data-ar-popup-stars in blocks/cercevo-ar-note.liquid).
 * buildStars() below generates the individual star <span> elements once per
 * dialog instance (cached via a WeakSet so re-opening the same dialog is free)
 * rather than rebuilding ~110 randomly-positioned elements on every open.
 */

/** Number of individual twinkling star dots layered over the dialog backdrop. */
const CERCEVO_STAR_COUNT = 110;

/**
 * Fills a stars container with randomly-placed, randomly-timed twinkling dots.
 * Idempotent per-element via a WeakSet, so calling this again for a container
 * that's already built is a cheap no-op.
 */
const cercevoBuiltStarContainers = new WeakSet();
function buildStars(container) {
  if (!container || cercevoBuiltStarContainers.has(container)) return;
  cercevoBuiltStarContainers.add(container);

  const fragment = document.createDocumentFragment();
  for (let i = 0; i < CERCEVO_STAR_COUNT; i += 1) {
    const star = document.createElement('span');
    star.className = 'cercevo-ar-qr-dialog__star';
    const size = (Math.random() * 1.6 + 0.8).toFixed(2); // 0.8px – 2.4px
    star.style.left = (Math.random() * 100).toFixed(2) + '%';
    star.style.top = (Math.random() * 100).toFixed(2) + '%';
    star.style.width = size + 'px';
    star.style.height = size + 'px';
    star.style.animationDelay = (Math.random() * 2.4).toFixed(2) + 's';
    star.style.animationDuration = (1.8 + Math.random() * 1.4).toFixed(2) + 's';
    star.style.opacity = (0.35 + Math.random() * 0.4).toFixed(2);
    fragment.appendChild(star);
  }
  container.appendChild(fragment);
}

class CercevoArLauncher extends HTMLElement {
  connectedCallback() {
    const iosLink = this.querySelector('[data-ar-ios-link]');
    const button = this.querySelector('[data-ar-button]');
    const popup = this.querySelector('[data-ar-popup]');
    if (!button) return;

    const ua = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isAndroid = /android/i.test(ua);

    if (isIOS && iosLink) {
      // Native <a rel="ar"> handles everything directly; the generic button would be redundant.
      button.hidden = true;
      return;
    }

    if (iosLink) iosLink.hidden = true;
    button.hidden = false;

    if (isAndroid) {
      button.addEventListener('click', () => {
        const glbUrl = this.dataset.glbUrl;
        if (!glbUrl) return;

        const fallbackUrl = window.location.href;
        const fileParam = encodeURIComponent(glbUrl);
        const fallbackParam = encodeURIComponent(fallbackUrl);
        const titleParam = encodeURIComponent(document.title || '');

        const intentUrl =
          'intent://arvr.google.com/scene-viewer/1.0?file=' +
          fileParam +
          '&mode=ar_preferred&title=' +
          titleParam +
          '#Intent;scheme=https;package=com.google.android.googlequicksearchbox;action=android.intent.action.VIEW;S.browser_fallback_url=' +
          fallbackParam +
          ';end;';

        const httpsFallbackUrl =
          'https://arvr.google.com/scene-viewer/1.0?file=' +
          fileParam +
          '&mode=ar_preferred&title=' +
          titleParam +
          '&link=' +
          fallbackParam;

        let handedOff = false;
        const onVisibilityChange = () => {
          if (document.hidden) handedOff = true;
        };
        document.addEventListener('visibilitychange', onVisibilityChange);

        window.location.href = intentUrl;

        window.setTimeout(() => {
          document.removeEventListener('visibilitychange', onVisibilityChange);
          if (!handedOff && !document.hidden) {
            window.open(httpsFallbackUrl, '_blank', 'noopener');
          }
        }, 1200);
      });
      return;
    }

    // Desktop / anything else: show a QR dialog that links back to this exact page.
    if (!popup) return;

    const qrHost = popup.querySelector('[data-ar-qr]');
    const starsHost = popup.querySelector('[data-ar-popup-stars]');
    let qrBuilt = false;

    const buildQr = () => {
      if (qrBuilt || !qrHost) return;
      qrBuilt = true;

      if (typeof window.qrcode !== 'function') {
        // cercevo-qrcode.js didn't load — fall back to text-only, same as before.
        popup.classList.add('cercevo-ar-qr-dialog--text-only');
        return;
      }

      try {
        const qr = window.qrcode(0, 'M');
        qr.addData(window.location.href);
        qr.make();
        qrHost.innerHTML = qr.createSvgTag({ cellSize: 5, margin: 8 });
      } catch (err) {
        popup.classList.add('cercevo-ar-qr-dialog--text-only');
      }
    };

    const closeBtn = popup.querySelector('[data-ar-popup-close]');
    const panel = popup.querySelector('[data-ar-popup-panel]');

    const closePopup = () => {
      popup.classList.remove('is-visible');
      window.setTimeout(() => {
        popup.hidden = true;
      }, 200);
      document.removeEventListener('keydown', onKeydown);
    };

    const onKeydown = (event) => {
      if (event.key === 'Escape') closePopup();
    };

    const openPopup = () => {
      buildQr();
      buildStars(starsHost);
      popup.hidden = false;
      window.requestAnimationFrame(() => popup.classList.add('is-visible'));
      document.addEventListener('keydown', onKeydown);
    };

    button.addEventListener('click', openPopup);

    if (closeBtn) {
      closeBtn.addEventListener('click', closePopup);
    }

    // Click on the backdrop (outside the panel) closes it too.
    popup.addEventListener('click', (event) => {
      if (panel && !panel.contains(event.target)) closePopup();
    });
  }
}

if (!customElements.get('cercevo-ar-launcher')) {
  customElements.define('cercevo-ar-launcher', CercevoArLauncher);
}
